import React, { useState, useEffect } from "react";
import { X, FileText, Save } from "lucide-react";
import styles from "../../_css/Agency.module.css";
import SectionDivider from "../Simulation/SectionDivider";
import FormInput from "../Simulation/FormInput";
import FormTextArea from "../Simulation/FormTextArea";
import TagInput from "../Simulation/TagInput";
import { font, FUNNEL_STAGES, LLM_PLATFORMS, LANGUAGES, DEFAULT_LLM_PLATFORM_IDS } from "../Simulation/constants";

function formatLanguage(v) {
  if (v == null || v === "") return "English";
  const s = String(v).trim();
  const found = LANGUAGES.find((l) => l.toLowerCase() === s.toLowerCase());
  if (found) return found;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

const EditSimulationModal = ({
  simulation: simulationProp,
  project,
  onClose,
  onSave,
}) => {
  const [loading, setLoading] = useState(false);
  const simulation = simulationProp || project;

  const getFunnelStageId = (sim) => {
    const funnelArr = Array.isArray(sim?.funnel) ? sim.funnel : [];
    const firstLabel = funnelArr[0];
    if (!firstLabel) return "awareness";
    const found = FUNNEL_STAGES.find((f) => f.label === firstLabel);
    return found?.id ?? "awareness";
  };

  const getLlmPlatforms = (sim) => {
    const fromList = Array.isArray(sim?.llm_platforms) ? sim.llm_platforms : null;
    if (fromList?.length) {
      const ids = fromList
        .map((x) => {
          const s = String(x).trim().toLowerCase();
          if (s === "chat-gpt") return "chatgpt";
          const byId = LLM_PLATFORMS.find((p) => p.id === s);
          if (byId) return byId.id;
          const byLabel = LLM_PLATFORMS.find(
            (p) => p.label.toLowerCase() === s,
          );
          return byLabel?.id;
        })
        .filter(Boolean);
      if (ids.length) return ids;
    }
    const plat = sim?.platform ?? sim?.llm_platform;
    if (plat) {
      const s = String(plat).trim().toLowerCase();
      if (s === "chat-gpt") return ["chatgpt"];
      const byId = LLM_PLATFORMS.find((p) => p.id === s);
      if (byId) return [byId.id];
    }
    return [...DEFAULT_LLM_PLATFORM_IDS];
  };

  const [form, setForm] = useState({
    id: simulation?.id ?? null,
    name: simulation?.name ?? "",
    description: simulation?.description ?? "",
    funnelStage: getFunnelStageId(simulation),
    competitorBrands: simulation?.competitor_brands ?? [],
    llmPlatforms: getLlmPlatforms(simulation),
  });
  const [competitors, setCompetitors] = useState(
    Array.isArray(simulation?.competitors) ? [...simulation.competitors] : [],
  );

  useEffect(() => {
    if (!simulation) return;
    setForm({
      id: simulation?.id ?? null,
      name: simulation.name ?? "",
      description: simulation.description ?? "",
      funnelStage: getFunnelStageId(simulation),
      competitorBrands: simulation?.competitor_brands ?? [],
      llmPlatforms: getLlmPlatforms(simulation),
    });
    setCompetitors(
      Array.isArray(simulation.competitors) ? [...simulation.competitors] : [],
    );
  }, [simulation]);

  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const selectFunnel = (id) => setForm((p) => ({ ...p, funnelStage: id }));
  const toggleLLM = (id) => {
    setForm((p) => ({
      ...p,
      llmPlatforms: p.llmPlatforms.includes(id)
        ? p.llmPlatforms.filter((s) => s !== id)
        : [...p.llmPlatforms, id],
    }));
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (onSave) {
      const funnelLabel = FUNNEL_STAGES.find(
        (f) => f.id === form.funnelStage,
      )?.label;
      setLoading(true);
      await onSave({
        id: simulation?.id,
        name: form.name.trim(),
        description: form.description?.trim() ?? "",
        funnelStage: funnelLabel,
        funnel: funnelLabel ? [funnelLabel] : [],
        competitors: [...competitors],
        llmPlatforms: [...form.llmPlatforms],
      });
      setLoading(false);
    } else {
      setLoading(false);
      onClose?.();
    }
    setLoading(false);
  };

  const overlayStyle = {
    background: "rgba(15, 23, 42, 0.45)",
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
  };
  const modalStyle = {
    maxWidth: 660,
    width: "100%",
    background: "#ffffff",
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 12,
    boxShadow: "0 20px 50px rgba(0,0,0,0.15)",
    display: "flex",
    flexDirection: "column",
    maxHeight: "calc(100vh - 32px)",
    fontFamily: font,
  };
  const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "20px 24px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
  };
  const titleStyle = {
    fontSize: 20,
    fontWeight: 700,
    color: "#363636",
    margin: 0,
    fontFamily: font,
  };
  const closeBtnStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    padding: 0,
    border: "none",
    background: "rgba(0,0,0,0.06)",
    borderRadius: 8,
    color: "#6b7280",
    cursor: "pointer",
    fontFamily: font,
  };
  const bodyStyle = {
    padding: "20px 22px",
    overflow: "auto",
    flex: 1,
    fontFamily: font,
  };
  const labelStyle = {
    display: "block",
    fontSize: 13,
    color: "#6b6b6b",
    marginBottom: 10,
    fontFamily: font,
  };
  const optionGridStyle = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
  };
  const optionCardStyle = (selected) => ({
    padding: "14px 16px",
    borderRadius: 10,
    cursor: "pointer",
    background: selected ? "rgba(239,68,68,0.08)" : "rgba(0,0,0,0.03)",
    border: selected
      ? "1px solid rgba(239,68,68,0.3)"
      : "1px solid rgba(0,0,0,0.08)",
    transition: "all 0.15s",
  });
  const optionCardHeadStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  };
  const optionLabelStyle = (selected) => ({
    fontSize: 13,
    fontWeight: 600,
    color: selected ? "#363636" : "#64748b",
    fontFamily: font,
  });
  const optionDescStyle = {
    fontSize: 11,
    color: "#999999",
    margin: 0,
    lineHeight: 1.4,
    fontFamily: font,
  };
  const checkStyle = {
    marginLeft: "auto",
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#ef4444",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    color: "#fff",
  };
  const llmCardStyle = (selected, available) => ({
    padding: "12px 16px",
    borderRadius: 10,
    cursor: available ? "pointer" : "not-allowed",
    opacity: available ? 1 : 0.4,
    background: selected ? "rgba(239,68,68,0.08)" : "rgba(0,0,0,0.03)",
    border: selected
      ? "1px solid rgba(239,68,68,0.3)"
      : "1px solid rgba(0,0,0,0.08)",
    transition: "all 0.15s",
  });
  const llmCardHeadStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
  };
  const dotStyle = (color) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: color,
  });
  const footerStyle = {
    display: "flex",
    gap: 10,
    marginTop: 20,
  };
  const saveBtnStyle = {
    padding: "12px 28px",
    borderRadius: 8,
    border: "none",
    background: "#ef4444",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: loading ? "not-allowed" : "pointer",
    fontFamily: font,
    display: "flex",
    alignItems: "center",
    gap: 6,
    opacity: loading ? 0.7 : 1,
  };
  const cancelBtnStyle = {
    padding: "12px 28px",
    borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.12)",
    background: "#fff",
    color: "#6b7280",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: font,
  };

  return (
    <div
      className={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      style={overlayStyle}
    >
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        style={modalStyle}
      >
        <div style={headerStyle}>
          <h2 style={titleStyle}>Edit Simulation</h2>
          <button
            type="button"
            onClick={onClose}
            style={closeBtnStyle}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div style={bodyStyle}>
          <SectionDivider label="Simulation Details" />
          <FormInput
            label="Simulation Name"
            placeholder="e.g., E.ON Sustainable Energy"
            icon={<FileText size={16} />}
            required
            value={form.name}
            onChange={(v) => u("name", v)}
          />
          <FormTextArea
            label="Simulation Description"
            placeholder="Describe the simulation scope..."
            value={form.description}
            onChange={(v) => u("description", v)}
          />
          <FormInput
            label="Language"
            value={formatLanguage(simulation?.language)}
            readOnly
            required={false}
          />

          <SectionDivider label="Competitor Tracking" />
          <TagInput
            label="Competitor Brands"
            placeholder="Type a competitor name and press Enter"
            helpText="Press Enter or comma to add."
            required
            tags={competitors}
            onAdd={(t) => setCompetitors((p) => [...p, t])}
            onRemove={(i) =>
              setCompetitors((p) => p.filter((_, idx) => idx !== i))
            }
          />

          <SectionDivider label="Simulation Settings" />
          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>
              Funnel Stage <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <div style={optionGridStyle}>
              {FUNNEL_STAGES.map((stage) => {
                const sel = form.funnelStage === stage.id;
                return (
                  <div
                    key={stage.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => selectFunnel(stage.id)}
                    onKeyDown={(e) =>
                      (e.key === "Enter" || e.key === " ") &&
                      selectFunnel(stage.id)
                    }
                    style={optionCardStyle(sel)}
                  >
                    <div style={optionCardHeadStyle}>
                      <span style={{ fontSize: 16, opacity: 0.8 }}>
                        {stage.icon}
                      </span>
                      <span style={optionLabelStyle(sel)}>{stage.label}</span>
                      {sel && (
                        <span style={checkStyle} aria-hidden>
                          ✓
                        </span>
                      )}
                    </div>
                    <p style={optionDescStyle}>{stage.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>
              LLM Platforms <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <div style={optionGridStyle}>
              {LLM_PLATFORMS.map((llm) => {
                const sel = form.llmPlatforms.includes(llm.id);
                return (
                  <div
                    key={llm.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleLLM(llm.id)}
                    onKeyDown={(e) =>
                      (e.key === "Enter" || e.key === " ") &&
                      toggleLLM(llm.id)
                    }
                    style={llmCardStyle(sel, true)}
                  >
                    <div style={llmCardHeadStyle}>
                      <span style={dotStyle(llm.color)} />
                      <span style={optionLabelStyle(sel)}>{llm.label}</span>
                      {sel && (
                        <span style={checkStyle} aria-hidden>
                          ✓
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={footerStyle}>
            <button
              type="button"
              onClick={handleSave}
              style={{
                ...saveBtnStyle,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
              disabled={loading}
            >
              <Save size={14} />
              {loading ? "Saving..." : "Save Changes"}
            </button>
            <button type="button" onClick={onClose} style={cancelBtnStyle}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditSimulationModal;
